/**
 * Utility functions for handling VOD thumbnails, especially for Kick platform
 */

// Default thumbnails that are always available
const KICK_DEFAULT_THUMBNAIL = '/kick-default-thumbnail.svg';
const TWITCH_DEFAULT_THUMBNAIL = '/twitch-default-thumbnail.svg';
const GENERIC_DEFAULT_THUMBNAIL = '/default-vod-thumbnail.svg';

// Twitch thumbnail patterns
const TWITCH_THUMBNAIL_PATTERNS = {
  // Standard Twitch VOD thumbnail pattern
  preview: (username: string, videoId: string) => 
    `https://static-cdn.jtvnw.net/cf_vods/d2nvs31859zcd8/twitchtracker_${username}_${videoId}_720.jpg`,
  // Alternative Twitch thumbnail pattern
  vod: (videoId: string) => 
    `https://static-cdn.jtvnw.net/previews-ttv/live_user_${videoId}-320x180.jpg`
};

// Extract platform and identifiers from VOD URL
export function extractVodInfo(vodUrl: string): {
  platform: 'kick' | 'twitch' | 'unknown';
  username?: string;
  videoId?: string;
} {
  try {
    const url = new URL(vodUrl);
    
    // Kick URL patterns: kick.com/username/video/video-id
    if (url.hostname.includes('kick.com')) {
      const pathParts = url.pathname.split('/');
      const username = pathParts[1];
      const videoId = pathParts[3];
      return {
        platform: 'kick',
        username,
        videoId
      };
    }
    
    // Twitch URL patterns: twitch.tv/videos/video-id or twitch.tv/username
    if (url.hostname.includes('twitch.tv')) {
      const pathParts = url.pathname.split('/');
      if (pathParts[1] === 'videos') {
        return {
          platform: 'twitch',
          videoId: pathParts[2]
        };
      } else {
        return {
          platform: 'twitch',
          username: pathParts[1]
        };
      }
    }
    
    return { platform: 'unknown' };
  } catch (error) {
    console.warn('[Thumbnail Utils] Failed to parse VOD URL:', vodUrl, error);
    return { platform: 'unknown' };
  }
}

// Generate fallback thumbnail URL based on VOD URL
export function generateFallbackThumbnail(vodUrl: string): string {
  const vodInfo = extractVodInfo(vodUrl);
  
  switch (vodInfo.platform) {
    case 'kick':
      // For Kick, always use the default thumbnail as fallback
      return KICK_DEFAULT_THUMBNAIL;
    
    case 'twitch':
      // For Twitch, try to generate a thumbnail URL if we have identifiers
      if (vodInfo.username && vodInfo.videoId) {
        return TWITCH_THUMBNAIL_PATTERNS.preview(vodInfo.username, vodInfo.videoId);
      }
      if (vodInfo.videoId) {
        return TWITCH_THUMBNAIL_PATTERNS.vod(vodInfo.videoId);
      }
      // Fallback to Twitch default thumbnail
      return TWITCH_DEFAULT_THUMBNAIL;
    
    default:
      // Generic fallback for unknown platforms
      return GENERIC_DEFAULT_THUMBNAIL;
  }
}

// Get reliable thumbnail URL with fallbacks
export function getReliableThumbnailUrl(vodUrl: string, originalThumbnail?: string | null): string {
  // If we have an original thumbnail, try to use it first
  if (originalThumbnail && originalThumbnail.trim()) {
    return originalThumbnail;
  }
  
  // Otherwise, generate a fallback based on the VOD URL
  return generateFallbackThumbnail(vodUrl);
}

// Check if a thumbnail URL is likely to be broken (for Kick specifically)
export function isThumbnailLikelyBroken(thumbnailUrl: string | null | undefined, vodUrl: string): boolean {
  if (!thumbnailUrl) return true;
  
  const vodInfo = extractVodInfo(vodUrl);
  
  // For Kick, only consider URLs broken if they're actually expired
  if (vodInfo.platform === 'kick') {
    // Check if it's a Kick S3 URL with pre-signed parameters
    if (thumbnailUrl.includes('kick-prod-videos.s3') || 
        thumbnailUrl.includes('amazonaws.com')) {
      // Check for S3 pre-signed URL parameters and if they're expired
      if (thumbnailUrl.includes('X-Amz-Expires') || 
          thumbnailUrl.includes('X-Amz-Signature')) {
        const isExpired = isS3UrlExpired(thumbnailUrl);
        // console.log('[Thumbnail Utils] Kick S3 URL expiration check:', {
        //   url: thumbnailUrl.substring(0, 100) + '...',
        //   isExpired,
        //   expiration: getS3UrlExpiration(thumbnailUrl)
        // });
        return isExpired;
      }
    }
    
    // For older Kick CDN URLs, we can assume they're potentially broken
    if (thumbnailUrl.includes('kick.com') && thumbnailUrl.includes('thumbnail')) {
      // console.log('[Thumbnail Utils] Old Kick CDN URL detected, using fallback');
      return true;
    }
  }
  
  // console.log('[Thumbnail Utils] Thumbnail considered valid:', {
  //   platform: vodInfo.platform,
  //   hasUrl: !!thumbnailUrl,
  //   urlStart: thumbnailUrl.substring(0, 50)
  // });
  return false;
}

// Parse S3 pre-signed URL expiration time
export function getS3UrlExpiration(url: string): Date | null {
  try {
    const urlObj = new URL(url);
    const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
    const dateParam = urlObj.searchParams.get('X-Amz-Date');
    
    if (expiresParam && dateParam) {
      // Parse the date from AWS format (YYYYMMDDTHHMMSSZ)
      const year = dateParam.substr(0, 4);
      const month = dateParam.substr(4, 2);
      const day = dateParam.substr(6, 2);
      const hour = dateParam.substr(9, 2);
      const minute = dateParam.substr(11, 2);
      const second = dateParam.substr(13, 2);
      
      const createdDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
      const expiresInSeconds = parseInt(expiresParam, 10);
      
      return new Date(createdDate.getTime() + (expiresInSeconds * 1000));
    }
  } catch (error) {
    console.warn('[Thumbnail Utils] Failed to parse S3 URL expiration:', error);
  }
  
  return null;
}

// Check if S3 pre-signed URL is expired
export function isS3UrlExpired(url: string): boolean {
  const expiration = getS3UrlExpiration(url);
  if (!expiration) return false;
  
  return new Date() > expiration;
}

// Check if URL is a cached thumbnail URL
export function isCachedThumbnailUrl(url: string): boolean {
  return url.includes('/api/thumbnails/cached/');
}

// Transform Kick thumbnail URL to use smaller resolution
export function transformKickThumbnailSize(thumbnailUrl: string, targetSize: number = 320): string {
  try {
    // Pattern: https://images.kick.com/video_thumbnails/[id1]/[id2]/[size].webp
    if (thumbnailUrl.includes('images.kick.com/video_thumbnails/')) {
      // For the new kick CDN format
      const regex = /^(https:\/\/images\.kick\.com\/video_thumbnails\/[^\/]+\/[^\/]+\/)(\d+)(\.webp)$/;
      const match = thumbnailUrl.match(regex);
      
      if (match) {
        const [, baseUrl, , extension] = match;
        return `${baseUrl}${targetSize}${extension}`;
      }
    }
    
    // For S3 URLs, return them as-is - let the browser handle if they're expired
    if (thumbnailUrl.includes('kick-prod-videos.s3') || thumbnailUrl.includes('amazonaws.com')) {
      return thumbnailUrl;
    }
    
    // For other formats, return as is
    return thumbnailUrl;
  } catch (error) {
    console.warn('[Thumbnail Utils] Failed to transform Kick thumbnail size:', error);
    return thumbnailUrl;
  }
}

// Utility function for handling thumbnail with fallbacks (not a React hook)
export function getThumbnailWithFallback(vodUrl: string, originalThumbnail?: string | null) {
  // If it's already a cached URL, use it directly
  if (originalThumbnail && isCachedThumbnailUrl(originalThumbnail)) {
    console.log('[Thumbnail Utils] Using cached thumbnail URL:', originalThumbnail);
    return {
      thumbnailUrl: originalThumbnail,
      isUsingFallback: false,
      fallbackUrl: generateFallbackThumbnail(vodUrl)
    };
  }
  
  const reliableThumbnail = getReliableThumbnailUrl(vodUrl, originalThumbnail);
  const shouldUseFallback = isThumbnailLikelyBroken(originalThumbnail, vodUrl);
  
  return {
    thumbnailUrl: shouldUseFallback ? generateFallbackThumbnail(vodUrl) : reliableThumbnail,
    isUsingFallback: shouldUseFallback || !originalThumbnail,
    fallbackUrl: generateFallbackThumbnail(vodUrl)
  };
}